import { useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Logo from "../assets/images/logo.png";
import { useAuth } from "../context/AuthContext";

const FAQ_ITEMS = [
  {
    section: "General",
    items: [
      { q: "Who can use Select Exposure?", a: "Individuals 18 years and older in the United States" },
      { q: "What if I live outside the United States?", a: "We plan to expand to other countries in the near future. However, for now, only residents of the United States are allowed to join Select Exposure." },
      { q: "Why can't I upload photos?", a: "To maintain the integrity of Select Exposure Contests, Contributors are only permitted to capture photos using the integrated website camera." },
      { q: "What do I do if I can't remember my password?", a: 'Click the "Forgot Password" icon on the home screen and the email instructions to change your password.' },
      { q: "I don't have access to the phone number linked to my account?", a: "For assistance with phone number related issues, please contact support@selectexposure.com and include your name, username, the email address and phone number used with creating your account." },
      { q: "How do I enable Two-Step Authentication?", a: "To keep your Select Exposure account secure, we recommend using a Two-Step Authentication. To utilize Two-Step Authentication you can either download an authenticator app/software on your mobile or desktop or complete the Two-Step Authentication using the phone number registered to your account and receive a verification code via SMS." },
      { q: "I think someone has accessed my account, what should I do?", a: "If you believe your account has been accessed by an unauthorized party, you should log in and change your password immediately. For enhanced security, we strongly recommend enabling Two-Step Authentication. For additional support, please email support@selectexposure.com." },
      { q: "What do I do about leaked or stolen content?", a: "Leaked or stolen content can be reported by emailing content@selectexposure.com. In your request, please include your name, screenname, address, phone number, email address, a description of your complaint, and the content URL." },
      { q: "What is the refund policy on Select Exposure?", a: "You can request a refund for any amount above $10, minus a 30% processing fee" },
      { q: "How do I request a refund?", a: "Send an email to Support@selectexposure.com with your refund request and justification" },
    ],
  },
  {
    section: "User",
    items: [
      { q: "How do I open a User account?", a: "Complete the User Registration process." },
      { q: "How do I access teaser galleries?", a: "You must have funds in your wallet to access teaser galleries" },
      { q: "Why do I have to be 18 years old to use Select Exposure?", a: "For legal purposes, to open an account, all Users and Contributors must be at least 18 years old and agree to Select Exposure's Terms of Service to use Select Exposure." },
      { q: "How do I delete my fan account?", a: 'To delete your account, visit My info and click "Delete Account". By deleting your account you are agreeing to relinquish any remaining account funds that have not been processed for payment.' },
      { q: "How do I view paid galleries?", a: "Enabling the purchase of Contributor body galleries is currently in development and will be available at a future date." },
      { q: "How do I withdraw funds?", a: "Users do not have the ability to withdraw funds. To request a refund, see our refund policy and process." },
      { q: "How do I invite Content Contributors?", a: "Use Select Exposure's Smoke Signal tool to anonymously invite a Content Contributor" },
      { q: "How do I find a contributor?", a: 'To find a Contributor, you can use the open text search to search for the Contributor\'s name or screen name. In addition, you can or use the search filters to search for your "type".' },
      { q: "How do I enter a contest?", a: "Use the Contest search to find your ideal contest and pay the entrance fee to view and judge the contest." },
      { q: "Can I download or copy content from Select Exposure?", a: "No. Select Exposure strictly prohibits the downloading or copying of content from its platform. Any attempt to download or copy content constitutes a violation of our Terms of Service." },
      { q: "Can I tip a Contributor?", a: "Currently, tips are not enabled on Select Exposure." },
      { q: "Can I Direct Message a Contributor?", a: "Currently, Direct Messaging is not enabled on Select Exposure." },
      { q: "How will payments made on Select Exposure appear on my card statement?", a: "Transactions will appear as Select Exposure LLC." },
      { q: "How do I use the Select Exposure wallet?", a: "Use the wallet function to add funds to your account from your debit or credit card." },
      { q: "Why am I being charged services fees on deposits to my Select Exposure wallet?", a: "We charge a small fee to cover costs and provide the best service possible. The fee for User deposits is always a flat $1.45 regardless of deposit amount." },
      { q: "How do I report a complaint or violation?", a: "You can flag an image for review using the dropdown menu on the photo. Other complaints or violations can be sent to violations@SelectExposure.com." },
    ],
  },
  {
    section: "Contributor",
    items: [
        {
        q: "How do I open a Contributor account?",
        a: (
          <ol>
            <li>1. Complete the Content Contributor Registration process</li>
            <li>2. Complete the W-9 tax form</li>
            <li>
              3. Complete the age &amp; identity verification process with our 3rd party
              provider, Yoti
            </li>
            <li>
              4. Link your bank account to Select Exposure so you can receive payments
            </li>
          </ol>
        ),
      },
      { q: "How many Contributor accounts can I have on Select Exposure?", a: "Currently, only one account is allowed per individual" },
      { q: "What countries are supported by Select Exposure?", a: "Currently, only the United States is supported" },
      {
        q: "Why was my contributor application rejected?",
        a: (
          <>
            <p>
              To become a contributor on Select Exposure you must complete the
              registration process. If you failed to do one of the following, your
              application was likely rejected.
            </p>

            <ol>
              <li>1. Did not complete all mandatory onboarding questions</li>
              <li>
                2. You did not capture the appropriate images to get approval from our
                KYC software
              </li>
            </ol>

            <p>
              For safety and compliance, Select Exposure reserves the right to
              reject any contributor application at any time and for any reason.
            </p>
          </>
        ),
      },
      { q: "How do I delete my Contributor account?", a: 'Visit My Info and click "Delete Account". By deleting your account, you agree to relinquish any remaining account funds that have not been processed for payment.' },
      { q: "What are payout percentages for Contributors?", a: "Contributors receive a 75% payout" },
      { q: "Why do Contributors receive 75% and not 80%?", a: 'Select Exposure is designed to help generate passive income for Contributors. Simply capture the perfect teaser gallery, body image galleries, then enable "auto-entry" on your favorite images and let your voters do the rest. No need for daily thirst traps, videos, or excessive promotions.' },
      { q: "What is Auto-Entry?", a: "With auto-entry, you can sit back and let us enter you into contests. To achieve maximum Contributor participation and the best User experience, Contributors with auto-entry enabled can be entered into qualifying contests, prior to a contest start time." },
      { q: "Can I join all contests?", a: "No. Contributors can only join contests where their attributes match the qualifying attributes a contest. Select Exposure reserves the right to disqualify any Contributor from a contest and/or withdraw any prize money and redistribute to the appropriate Contributor(s)." },
      { q: "What content is allowed on Select Exposure?", a: "Content on Select Exposure is at the discretion of the contributor. However, when entering a specific contest, the body part listed on the contest must be visible. Including other body parts, such face or identifying marks is up to the contributor." },
      { q: "Why was my content deactivated?", a: "If your content was deactivated it was due to a violation of the Select Exposure Terms. To appeal a deactivation, please email support@selectexposure.com." },
      { q: "Can I post nude content on Select Exposure?", a: "Yes, the level of nudity posted is at the discretion of the Contributor." },
      { q: "Can I use my Select Exposure account to feature someone else?", a: "No, you are only permitted to post images of yourself on Select Exposure" },
      { q: "Can I post AI content on Select Exposure?", a: "No, you are only permitted to post unaltered and unedited images of yourself on Select Exposure" },
      { q: "Can I post AI-generated, altered or enhanced content of me or someone else on Select Exposure?", a: "No, you are only permitted to post photos of yourself on Select Exposure" },
      { q: "Can I establish my own content price on Select Exposure?", a: "Future functionality on Select Exposure will allow Contributors to establish their own content price for body photo galleries." },
      { q: "Are tips allowed on Select Exposure?", a: "Currently, tips are not enabled on Select Exposure." },
      { q: "How do I promote my Select Exposure account on other platforms?", a: "Contributors can promote their Select Exposure profile using their username. This will help drive content participation and future purchase of body photo galleries." },
      { q: "How do I get paid on my earnings on Select Exposure?", a: "To get paid by Select Exposure you must link your bank account so we can wire payments to your account" },
      {
        q: "How long does it take to get paid on my earnings?",
        a: (
          <>
            <p>
              Select Exposure has 2 payment cycles each month. Contributors should
              expect to receive funds within 7 days following the end of the cycle.
            </p>

            <p className="mt-4">
              1<sup>st</sup> cycle: The 1<sup>st</sup> of the month through the 15
              <sup>th</sup> of the month
            </p>

            <p className="mt-4">
              2<sup>nd</sup> cycle: The 16<sup>th</sup> of the month through the end
              of the month
            </p>
          </>
        ),
      },
      { q: "Why am I being charged a fee by my bank for payments on Select Exposure?", a: "The balance displayed in your Wallet represents the exact amount in USD that will be transferred to your selected payment method. Please note that your bank or financial institution may impose additional fees for receiving funds. Select Exposure has no control over, nor responsibility for, any fees charged by financial institutions or payment providers." },
      { q: "Do I have to pay taxes on my earnings from Select Exposure?", a: "You are responsible for paying any taxes owed on your Select Exposure earnings. Depending on your location and total income, your earnings through Select Exposure may be subject to taxation. Please note that Select Exposure does not provide tax advice." },
      { q: "Why am I being asked to provide Select Exposure with my tax number/identity information?", a: "Select Exposure may be required by law to disclose information about your earnings to the tax authorities in your state of residence." },
      { q: "How do I request a 1099 Form?", a: "U.S. residents who have earned and withdrawn more than $600 USD during the prior tax year will be issued an IRS Form 1099 by Select Exposure. This form will be filed with the IRS and delivered to the address provided on your W-9 or sent to the email address provided at registration. To ensure proper delivery of your tax documents, please update your W-9 form if your mailing address changes." },
      { q: "How do I update my 1099 Form?", a: "You may update your W-9 form until December 31 of the applicable tax year. After this date, your IRS Form 1099 will be prepared using the information currently on file. If you require corrections to the information reported on your Form 1099, please contact support@selectexposure.com." },
    ],
  },
];

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(null);
  const auth = useAuth();
  const hasAppShell = Boolean(auth?.profile?.accessToken);

  const faqContent = (
    <div className="max-w-4xl mx-auto py-6">
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-8">Frequently Asked Questions</h1>
      {FAQ_ITEMS.map((section, sectionIdx) => (
        <div key={sectionIdx} className="mb-10">
          <h2 className="text-xl font-semibold text-[#ceb46a] mb-4 border-b border-gray-600 pb-2">
            {section.section}
          </h2>
          <div className="space-y-2">
            {section.items.map((item, itemIdx) => {
              const index = `${sectionIdx}-${itemIdx}`;
              const isOpen = openIndex === index;
              const hasAnswer = item.a && (typeof item.a === "string" ? item.a.trim() !== "" : true);
              return (
                <div
                  key={itemIdx}
                  className="rounded-lg border border-gray-600/50 bg-gray-800/30 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full px-4 py-3 text-left flex justify-between items-center gap-2 text-white hover:bg-gray-700/30 transition"
                  >
                    <span className="font-medium">{item.q}</span>
                    {hasAnswer && (
                      <span className="text-gray-400 shrink-0">
                        {isOpen ? "−" : "+"}
                      </span>
                    )}
                  </button>
                  {hasAnswer && isOpen && (
                    <div className="px-4 py-3 border-t border-gray-600/50 bg-gray-900/30 text-gray-300 whitespace-pre-line">
                      {typeof item.a === "string" ? item.a : item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (!hasAppShell) {
    return (
      <div className="min-h-screen bg-black px-4 py-8 text-white sm:px-8">
        <div className="mx-auto max-w-5xl">
          <header className="mb-8 flex flex-col gap-4 border-b border-gray-800 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/login" className="inline-flex items-center">
              <img src={Logo} alt="Select Exposure" className="w-[240px] max-w-full" />
            </Link>
            <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-gray-300">
              <Link to="/login" className="hover:text-[#ceb46a]">
                Login
              </Link>
              <Link to="/signup" className="hover:text-[#ceb46a]">
                Sign Up
              </Link>
              <Link to="/conditions" className="hover:text-[#ceb46a]">
                Terms &amp; Conditions
              </Link>
            </nav>
          </header>
          {faqContent}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-6 lg:ml-64">
        <Topbar pageTitle="FAQs" />
        {faqContent}
      </div>
    </div>
  );
}
